import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { ApiError, api, setTenantSlug } from '@/api/client'
import type { CurrentTenant, CurrentUser, Session } from '@/api/types'


export const useAuthStore = defineStore('auth', () => {
  const user = ref<CurrentUser | null>(null)
  const tenant = ref<CurrentTenant | null>(null)
  const loading = ref(false)

  /** False until the first session probe finishes, so the router can wait instead of guessing. */
  const initialized = ref(false)

  const isAuthenticated = computed(() => user.value !== null)
  const isAdmin = computed(() =>
    (user.value?.roles ?? []).some((role) => role === 'Owner' || role === 'Admin'),
  )
  const isOwner = computed(() => (user.value?.roles ?? []).includes('Owner'))

  /** A customer contact rather than someone who works here. Decides which half of the app loads. */
  const isCustomerContact = computed(() => user.value?.isCustomerContact === true)

  function apply(session: Session) {
    user.value = session.user
    tenant.value = session.tenant
    setTenantSlug(session.tenant.slug)
  }

  function clear() {
    user.value = null
    tenant.value = null
  }

  /** Probes the session cookie. A 401 here is the normal "not signed in" answer, not an error. */
  async function restore(): Promise<void> {
    if (initialized.value) return
    loading.value = true
    try {
      apply(await api.get<Session>('/api/auth/session'))
    } catch (error) {
      clear()
      if (!(error instanceof ApiError) || !error.isUnauthorized) {
        console.warn('Session probe failed', error)
      }
    } finally {
      loading.value = false
      initialized.value = true
    }
  }

  async function login(workspace: string, email: string, password: string, rememberMe: boolean) {
    // Set the tenant first: the login request itself has to carry X-Tenant-Slug so the server
    // knows which workspace's user table to look in.
    setTenantSlug(workspace)
    loading.value = true
    try {
      apply(await api.post<Session>('/api/auth/login', { email, password, rememberMe }))
    } finally {
      loading.value = false
      initialized.value = true
    }
  }

  /** A session that arrived some other way than the password form — the portal's code, say. */
  function applySession(session: Session) {
    apply(session)
    initialized.value = true
  }

  async function logout() {
    // Before the session goes: forgetting the push subscription needs an authenticated call, and
    // a row left behind would keep pushing this person's work to a browser they have signed out
    // of — which on a shared laptop is somebody else's screen.
    await unsubscribeFromPush()

    try {
      await api.post('/api/auth/logout')
    } finally {
      clear()
    }
  }

  async function unsubscribeFromPush() {
    try {
      const { unsubscribe } = await import('@/lib/push')
      await unsubscribe()
    } catch {
      // Never block a sign-out. The subscription is deleted by the first failed delivery anyway,
      // and the person in front of the screen wants to be signed out now.
    }
  }

  async function updateProfile(email: string, firstName: string, lastName: string) {
    const session = await api.put<Session>('/api/auth/profile', {
      email,
      firstName: firstName || null,
      lastName: lastName || null,
    })
    apply(session)
  }

  return {
    user,
    tenant,
    loading,
    initialized,
    isAuthenticated,
    isAdmin,
    isCustomerContact,
    isOwner,
    restore,
    login,
    applySession,
    logout,
    updateProfile,
  }
})
